import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { OperationButton } from '../../OperationButton';
import { sendAccessToken } from '../../../../../api/mutations';
import useAuthPostMessage from '../../../../../hooks/useAuthPostMessage';
import { ReportModal } from '../ReportModal';
import { buttonStyles, ReportError, ReportSectionTextarea } from './styled';
import { setCurrentItemAuthModalOpen } from '../../../../../actions';

interface ReportInputModalProps {
  isOpenModal?: boolean;
  isDisabledBtn: boolean;
  onCloseModal: () => void;
  onSubmitModal: (report: { report: string }) => void;
  onInputChange: () => void;
  title: string;
  error: string;
  isAuthenticated: boolean;
  handleBack: () => void;
}

export const ReportInputModal: React.FC<ReportInputModalProps> = ({
  isOpenModal = true,
  isDisabledBtn,
  onCloseModal,
  onSubmitModal,
  onInputChange,
  title,
  error,
  isAuthenticated,
  handleBack
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isFocused, setFocused] = useState({ report: false });
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated);
  const [report, setReport] = useState('');

  const [mutateToken] = useMutation(sendAccessToken, {
    onCompleted: data => {
      onSubmitModal({ report });
      if (!data?.signIn?.errors?.length) {
        setIsLoggedIn(true);
      }
    },
    onError: error => {
      throw new Error(error.message);
    },
    update(cache, { data: { signIn } }) {
      cache.modify({
        fields: {
          profile(existing) {
            if (!signIn?.errors?.length) {
              return {
                ...signIn.company
              };
            }
            return existing;
          }
        }
      });
    }
  });

  const handleOpenAuthModal = (isOpen: boolean) => {
    dispatch(setCurrentItemAuthModalOpen(isOpen));
  };

  useAuthPostMessage({
    handler: event => {
      if (event.data.name === 'authenticationFinished') {
        if (event.data.access_token) {
          handleOpenAuthModal(false);
          mutateToken({
            variables: { accessToken: event.data.access_token }
          });
        }
      } else if (event.data === 'authenticationModalClose') {
        handleOpenAuthModal(false);
      }
    }
  });

  const onReportChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onInputChange();
    setReport(e.target.value);
  };

  const onFocusReportInput = () => {
    setFocused({ ...isFocused, report: true });
  };

  const onBlurReportInput = () => {
    setFocused({ ...isFocused, report: false });
  };

  const onCloseModalClick = () => {
    setReport('');
    onCloseModal();
  };

  const handleSubmit = () => {
    if (isLoggedIn) {
      onSubmitModal({ report });
    } else {
      handleOpenAuthModal(true);
    }
  };

  useEffect(() => {
    setIsLoggedIn(isAuthenticated);
  }, [isAuthenticated]);

  return (
    <ReportModal
      title={title}
      isOpen={isOpenModal}
      closeHandler={onCloseModalClick}
      handleBack={handleBack}
      isInputModal
    >
      <ReportSectionTextarea
        rows={6}
        name="report-text"
        placeholder={t('pages.current_item.reason_for_complaint')}
        onChange={onReportChangeHandler}
        onFocus={onFocusReportInput}
        onBlur={onBlurReportInput}
        value={report}
        autoComplete="off"
        tabIndex={-1}
        $isError={!!error}
      />

      {error && <ReportError tag="body_2">{error}</ReportError>}
      <OperationButton
        text={t('pages.current_item.buttons.send')}
        onClickHandler={handleSubmit}
        disabled={isDisabledBtn}
        styles={buttonStyles}
      />
    </ReportModal>
  );
};
